import {
  calcPrice,
  findLojaForBrandPair,
  getSegmentCode,
  isBiFolderSegment,
  isNroSegmentoPrecoCuringa,
  normalizeParametroPrecoBase,
  normalizePrecoListForCalc,
  CalcPriceOptions,
} from '@/shared';
import { useCallback, useMemo, useRef } from 'react';
import { useCart, useClient, usePrices, useTables, useCorporateFlags, useSelectedClientScope } from '@/hooks';
import {
  ProductProps,
  PriceProductProps,
  TaxSubstitutionProps,
  ParametroPrecoBaseProps,
  PercentualFornecedorProps,
  PrecoPromocaoClienteProps,
} from '@/types';

const MAX_CALCULATED_CACHE_KEYS = 1500;
const MAX_CALCULATED_CACHE_KEYS_PER_CLIENT = 600;
// Flag temporária para rollback rápido da regra global de indisponíveis.
const TEMP_HIDE_UNAVAILABLE_PRODUCTS = false;

export const useCalcPrice = () => {
  const { tables } = useTables();
  const { getLimit } = useCart();

  const { selectedBrandPair: selectedBrand } = useCorporateFlags();

  const { selectedClient } = useClient();

  const baseSelectedClientScope = useSelectedClientScope();

  const { catalog: hookCatalog, setProducts, isLoaded, isLoading: isLoadingCatalog, calculatedList } = usePrices();

  const pruneCalculatedListCache = useCallback(
    (clientCode: number) => {
      const clientPrefix = `${clientCode}-`;
      const keys = Object.keys(calculatedList.current);
      const clientKeys = keys.filter((key) => key.startsWith(clientPrefix));

      if (clientKeys.length > MAX_CALCULATED_CACHE_KEYS_PER_CLIENT) {
        const extraClientKeys = clientKeys.length - MAX_CALCULATED_CACHE_KEYS_PER_CLIENT;
        for (let i = 0; i < extraClientKeys; i++) {
          delete calculatedList.current[clientKeys[i]];
        }
      }

      const allKeys = Object.keys(calculatedList.current);
      if (allKeys.length > MAX_CALCULATED_CACHE_KEYS) {
        const extraGlobalKeys = allKeys.length - MAX_CALCULATED_CACHE_KEYS;
        for (let i = 0; i < extraGlobalKeys; i++) {
          delete calculatedList.current[allKeys[i]];
        }
      }
    },
    [calculatedList]
  );

  const substTrib = tables.current.cod_regime_tributario;
  const selectedLojaFromBrand = useMemo(
    () => findLojaForBrandPair(selectedClient, selectedBrand),
    [selectedClient, selectedBrand]
  );

  const selectedClientScope = useMemo(() => {
    if (!baseSelectedClientScope?.COD_CLIENTE) return baseSelectedClientScope;
    if (!selectedLojaFromBrand) return baseSelectedClientScope;

    const codSegmentoFromBrand = Number(selectedBrand?.codSegmento);
    const segmentosLoja = Array.isArray(selectedLojaFromBrand.COD_SEGMENTO) ? selectedLojaFromBrand.COD_SEGMENTO : [];
    const segmentoAtivo = segmentosLoja.find((segmento: any) => Number(getSegmentCode(segmento)) === codSegmentoFromBrand);

    const payLoja = selectedLojaFromBrand.COD_PAGAMENTO;
    const payBase = baseSelectedClientScope.COD_PAGAMENTO;
    const COD_PAGAMENTO =
      Array.isArray(payLoja) && payLoja.length > 0
        ? payLoja
        : Array.isArray(payBase) && payBase.length > 0
          ? payBase
          : Array.isArray(payLoja)
            ? payLoja
            : Array.isArray(payBase)
              ? payBase
              : [];
    const isBiFolder =
      isBiFolderSegment(codSegmentoFromBrand) || isBiFolderSegment(getSegmentCode(segmentoAtivo));
    return {
      ...baseSelectedClientScope,
      ...selectedLojaFromBrand,
      COD_SEGMENTO: isBiFolder ? selectedLojaFromBrand.COD_SEGMENTO : segmentoAtivo ? [segmentoAtivo] : segmentosLoja,
      COD_PAGAMENTO,
    };
  }, [baseSelectedClientScope, selectedLojaFromBrand, selectedBrand?.codSegmento]);

  const segmentos = useMemo(
    () => selectedClientScope?.COD_SEGMENTO?.map((c: any) => getSegmentCode(c)) || [],
    [selectedClientScope]
  );

  const condicoes = useMemo(() => {
    const fromPayments = (pay: unknown) =>
      (Array.isArray(pay) ? pay : [])
        .map((i: any) => Number(i?.cod_condicao ?? i?.COD_CONDICAO))
        .filter((n: number) => Number.isFinite(n) && n > 0);

    const scoped = fromPayments(selectedClientScope?.COD_PAGAMENTO);
    if (scoped.length > 0) {
      return scoped;
    }
    // Multi-loja: escopo às vezes ainda não carrega `COD_PAGAMENTO` da loja; sem condições o `handlePrices` não itera nada.
    return fromPayments(selectedClient?.COD_PAGAMENTO);
  }, [selectedClientScope, selectedClient?.COD_PAGAMENTO]);

  /** Mesma convenção que `SearchPage` / carrinho: loja+segmento da bandeira ativa (evita drift vs `segmentos[0]`). */
  const activeBrandKey = useMemo(() => {
    const pairLoja = Number(selectedBrand?.codLoja);
    const pairSeg = Number(selectedBrand?.codSegmento);
    if (Number.isFinite(pairLoja) && pairLoja > 0 && Number.isFinite(pairSeg) && pairSeg > 0) {
      return `${pairLoja}:${pairSeg}`;
    }

    const loja = Number(selectedClientScope?.COD_LOJA);
    const seg = segmentos?.[0];
    if (Number.isFinite(loja) && loja > 0 && seg != null && Number.isFinite(Number(seg))) {
      return `${loja}:${Number(seg)}`;
    }

    return `${selectedClientScope?.COD_LOJA || ''}:${segmentos?.[0] || ''}`;
  }, [selectedBrand?.codLoja, selectedBrand?.codSegmento, selectedClientScope?.COD_LOJA, segmentos]);
  /** Atualizado a cada render — `getCatalog` compara snapshot após `await` para descartar resultado obsoleto. */
  const catalogScopeKeyRef = useRef('');
  catalogScopeKeyRef.current = `${selectedClient?.COD_CLIENTE ?? ''}:${activeBrandKey}`;
  const catalog = useMemo(() => {
    const cod = selectedClient?.COD_CLIENTE as number | undefined;
    if (cod == null) return [];
    return hookCatalog[cod]?.[activeBrandKey] ?? [];
  }, [hookCatalog, selectedClient?.COD_CLIENTE, activeBrandKey]);

  /** Evita dependência de `catalog` em `getCatalog`/`loadStates` (atualizações do catálogo recriariam o callback e re-disparariam efeitos). */
  const catalogForActiveScopeLenRef = useRef(0);
  catalogForActiveScopeLenRef.current = catalog.length;

  const handlePrices = useCallback(
    (
      condicoes: number[],
      product: ProductProps,
      parametroPrecoBase: ParametroPrecoBaseProps[],
      percentualFornecedor: PercentualFornecedorProps[],
      listSubstTrib: TaxSubstitutionProps[],
      precoPromocao: PrecoPromocaoClienteProps,
      from: string,
      options?: CalcPriceOptions
    ): PriceProductProps[] => {
      if (!selectedClientScope?.COD_CLIENTE || !product?.COD_PRODUTO) return [] as PriceProductProps[];

      // PRECO: array, mapa por QTD_EMBALAGEM ou aninhado ({ [qtd]: leaf }); precisa de QTD_EMBALAGEM para casar EMBALAGEM.
      const rawPrecos: any = product?.PRECO || {};
      const precoList: any[] = normalizePrecoListForCalc(rawPrecos).map((row) => ({
        ...row,
        COD_PRODUTO: (row as any).COD_PRODUTO ?? product.COD_PRODUTO,
      }));
      // Guard antigo `isLoaded.current[catalogLoadedKey]` retornava `[]` para qualquer produto
      // de um escopo previamente marcado como vazio. Isso quebrava o carrinho/checkout, que
      // mostram itens cujo escopo (loja+segmento) pode ser diferente da bandeira ativa atual.
      // Listas (Products/Category/SearchPage/FilterBar) iteram sobre o `catalog` filtrado, que
      // já é vazio nesses casos — então remover o guard não as afeta.
      // A nova checagem é baseada no próprio dado do produto: sem PRECO raw, sem cálculo.
      if (!precoList.length) return [] as PriceProductProps[];

      const precos = [] as PriceProductProps[];
      const baseList = Array.isArray(parametroPrecoBase) ? parametroPrecoBase : [];
      const tribList = Array.isArray(listSubstTrib) ? listSubstTrib : [];

      const normalizeFaixaKey = (f: unknown) =>
        String(f ?? '')
          .trim()
          .toUpperCase();

      const precoPromocaoByClient = precoPromocao?.[selectedClientScope.COD_CLIENTE] || {};

      // Indexa por condição + faixa financeira (chave normalizada — evita "A" vs "a" / espaços).
      const parametroPrecoBaseByCondFaixa = baseList.reduce((acc, p) => {
        const condMap = acc.get(p.NROCONDICAOPAGTO) || new Map<string, ParametroPrecoBaseProps>();
        condMap.set(normalizeFaixaKey(p.FAIXAACRFINANCEIRO), p);
        acc.set(p.NROCONDICAOPAGTO, condMap);
        return acc;
      }, new Map<number, Map<string, ParametroPrecoBaseProps>>());

      const percentualFornecedorBySeq = new Map<number, number>(
        (percentualFornecedor || []).map((f) => [f.SEQFAMILIA, f.PERACRESCDESC] as const)
      );

      const precosFiltrados = precoList.filter((p: any) => {
        const lojaCliente = selectedClientScope?.COD_LOJA;
        const codLojaOk = p?.COD_LOJA == null || (lojaCliente != null && Number(p.COD_LOJA) === Number(lojaCliente));

        const seg = p?.NRO_SEGMENTO;
        // Alinha com Categories (sem segmentos no cliente = não filtra por segmento) e com curingas ERP (998/999).
        const segmentoOk =
          seg == null ||
          isNroSegmentoPrecoCuringa(seg) ||
          segmentos.length === 0 ||
          segmentos.some((s) => Number(s) === Number(seg));

        return codLojaOk && segmentoOk;
      });

      const safeCondicoes = Array.isArray(condicoes) ? condicoes : [];
      for (const cond of safeCondicoes) {
        for (const precoJson of precosFiltrados) {
          // Preço promocional por quantidade de embalagem (base unitária)
          const precoPromo =
            precoPromocaoByClient?.[precoJson.QTD_EMBALAGEM]?.[precoJson.COD_PRODUTO]?.[selectedClientScope.COD_LOJA]?.[
              precoJson.NRO_SEGMENTO
            ];
          const priceValidation: any = precoPromo?.VLR_PRECO ? precoPromo : precoJson;

          const condMap = parametroPrecoBaseByCondFaixa.get(cond);
          const faixaKey = normalizeFaixaKey(priceValidation.FAIXAACRFINANCEIRO);
          let percPreco = condMap?.get(faixaKey);
          if (!percPreco && condMap?.size) {
            const firstEntry = condMap.values().next().value as ParametroPrecoBaseProps | undefined;
            if (faixaKey === '' && firstEntry) {
              percPreco = firstEntry;
            }
          }
          if (!percPreco && condMap?.size) {
            percPreco = condMap.get('A') || condMap.get(normalizeFaixaKey('A'));
          }
          if (!percPreco && condMap?.size === 1) {
            percPreco = Array.from(condMap.values())[0];
          }

          if (!percPreco) {
            continue;
          }

          const embalagemBase = (product.EMBALAGEM || []).find((e) => e.QTD_EMBALAGEM === precoJson.QTD_EMBALAGEM);
          // Apenas adiciona fallback para múltiplos de venda (PK) quando houver
          const embalagensPK = (product.EMBALAGEM || []).filter(
            (e) => e.SG_EMBALAGEM === 'PK' && (e.QTD_MULTIPLO_VENDA || 0) > 1 && e.QTD_EMBALAGEM === precoJson.QTD_EMBALAGEM
          );
          let embalagens = [embalagemBase, ...embalagensPK].filter(Boolean) as any[];
          if (!embalagens.length && precoJson.QTD_EMBALAGEM != null) {
            const sg = String((precoJson as any).SG_EMBALAGEM || 'UN') as any;
            embalagens = [
              {
                QTD_EMBALAGEM: precoJson.QTD_EMBALAGEM,
                SG_EMBALAGEM: sg,
                QTD_MULTIPLO_VENDA:
                  Number((precoJson as any).QTD_MULTIPLO_VENDA) > 0 ? Number((precoJson as any).QTD_MULTIPLO_VENDA) : 1,
                PERC_MAX_FLEX: 0,
                PERC_MIN_FLEX: 0,
              },
            ];
          }

          // Itera por TODAS as embalagens compatíveis, gerando preço também para múltiplos de venda (PK)
          for (const embalagem of embalagens) {
            const percentualForcedor = percentualFornecedorBySeq.get(precoJson.SEQFAMILIA) || 0;

            const isPrecoPromo =
              precoPromo && precoPromo.QTD_EMBALAGEM === embalagem?.QTD_EMBALAGEM && precoPromo.VLR_PRECO < precoJson.VLR_PRECO;

            const basePriceJson = isPrecoPromo ? precoPromo : precoJson;

            const precoIn: PriceProductProps = {
              ...precoJson,
              ...(isPrecoPromo && { COD_CLIENTE: (precoPromo as any).COD_CLIENTE }),
              COD_LOJA: selectedClientScope.COD_LOJA,
              COD_PRODUTO: precoJson.COD_PRODUTO,
              COD_TRIBUTACAO_PRODUTO: basePriceJson.COD_TRIBUTACAO_PRODUTO,
              DESTACA_ST: basePriceJson.DESTACA_ST,
              DT_FIM: '',
              DT_INICIO: '',
              FAIXAACRFINANCEIRO: basePriceJson.FAIXAACRFINANCEIRO,
              INDUSAACRESCTABVENDA: basePriceJson.INDUSAACRESCTABVENDA,
              NROCONDICAOPAGTO: cond,
              NRO_SEGMENTO: basePriceJson.NRO_SEGMENTO,
              // Usa a embalagem corrente: para PK garante QTD_MULTIPLO_VENDA correta
              QTD_EMBALAGEM: embalagem.QTD_EMBALAGEM,
              QTD_MULTIPLO_VENDA: embalagem.QTD_MULTIPLO_VENDA,
              SEQFAMILIA: basePriceJson.SEQFAMILIA,
              NROTABVENDA: `${selectedClientScope.COD_TABELA_VENDA}`,
              PERACRESCDESC: percentualForcedor,
              PERACRENTREGA: percPreco.PERACRENTREGA,
              PERACRFINANCEIRO: percPreco.PERACRFINANCEIRO,
              SG_EMBALAGEM: embalagem.SG_EMBALAGEM,
              TP_PROMO: basePriceJson.TP_PROMO,
              // Valor base por unidade; calcPrice cuidará de multiplicar por QTD_MULTIPLO_VENDA
              VLR_PRECO: basePriceJson.VLR_PRECO,
              // Pauta em nível unitário; calcPrice/ST cuidará da multiplicação/uso correto
              VLR_PAUTA: precoJson.VLR_PAUTA,
            } as PriceProductProps;

            precos.push(precoIn);
          }
        }
      }

      const calculatedPrices = calcPrice(selectedClientScope, precos, tribList, options);

      return calculatedPrices;
    },
    [selectedClientScope, segmentos]
  );

  const getProduct = useCallback(
    (id: number) => {
      return catalog?.find((item) => item?.COD_PRODUTO === id);
    },
    [catalog]
  );

  const findPrice = (price: PriceProductProps[], option_uid: string, paymentCondition: number) => {
    if (!price || !Array.isArray(price) || !price.length) return {} as PriceProductProps;
    const [packing, quantity] = option_uid?.split('_') || [];
    const correctPrice = price
      .filter((p) => p.COD_LOJA === selectedClientScope?.COD_LOJA)
      ?.find((p) => {
        // Para múltiplos de venda (ex.: PK), procurar pela própria embalagem e pelo QTD_MULTIPLO_VENDA
        if (packing === 'PK' || (p?.QTD_MULTIPLO_VENDA || 0) > 1) {
          return (
            p.SG_EMBALAGEM === packing && p.QTD_MULTIPLO_VENDA?.toString() === quantity && p.NROCONDICAOPAGTO === paymentCondition
          );
        }
        // Caso padrão (UN ou outras embalagens sem múltiplo)
        return p.SG_EMBALAGEM === packing && p.QTD_EMBALAGEM?.toString() === quantity && p.NROCONDICAOPAGTO === paymentCondition;
      });
    return correctPrice || ({} as PriceProductProps);
  };

  const getPrices = useCallback(
    (product: ProductProps): PriceProductProps[] => {
      if (!selectedClientScope?.COD_CLIENTE || !product?.COD_PRODUTO) return [] as PriceProductProps[];

      const condicoesSig = condicoes.length ? [...condicoes].sort((a, b) => a - b).join('_') : 'none';
      const tabSig = String(selectedClientScope?.COD_TABELA_VENDA ?? '');
      const keyCalculated = `${selectedClientScope.COD_CLIENTE}-${activeBrandKey}-${tabSig}-${condicoesSig}-${product.COD_PRODUTO}-p2`;
      if (calculatedList.current[keyCalculated]) {
        const cached = calculatedList.current[keyCalculated];
        // Refresca a ordem de inserção para priorizar itens usados recentemente.
        delete calculatedList.current[keyCalculated];
        calculatedList.current[keyCalculated] = cached;
        return cached;
      }

      const codLoja = selectedClientScope.COD_LOJA;
      const tabVenda = selectedClientScope.COD_TABELA_VENDA;
      const percentual = (tables.current.percentual_fornecedor as any) ?? [];
      const listSubstTrib = (tables.current.cod_regime_tributario as any) ?? [];
      const parametroPrecoPromocao = tables.current.precoPromocaoCliente;
      const percentualFornecedor = (Array.isArray(percentual) ? percentual : []).filter(
        (p: PercentualFornecedorProps) => `${p.NROTABVENDA}` === `${tabVenda}`
      );

      const parametroPreco = normalizeParametroPrecoBase(tables.current.parametro_preco_base, codLoja, tabVenda, condicoes);

      const precos = handlePrices(
        condicoes,
        product,
        parametroPreco,
        percentualFornecedor,
        listSubstTrib,
        parametroPrecoPromocao,
        'useCalcPrice'
      );

      if (precos.length > 0) {
        calculatedList.current[keyCalculated] = precos;
        pruneCalculatedListCache(selectedClientScope.COD_CLIENTE);
      }
      return precos;
    },
    [selectedClientScope, pruneCalculatedListCache, condicoes, tables, handlePrices, activeBrandKey]
  );

  return {
    catalog,
    substTrib,
    isLoadingCatalog,
    isLoaded: isLoaded.current,
    getProduct,
    handlePrices,
    findPrice,
    getPrices,
  };
};
